import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, readJson } from "../../lib/api";
import { formatMoney } from "../../lib/engage";
import { useToast } from "../Toast";

// Owner-only payroll: what each person is owed (hourly on active trim time +
// piece rate + bonuses), with mark-paid / bonus actions. Money is integer cents.

type PayRun = {
  id: number; started_at: number /* epoch ms */; active_ms: number; grams: number; paid_at: number | null;
  hourly_cents_due: number; piece_cents_due: number;
};
type PayBonus = { id: number; amount_cents: number; note: string | null; created_at: number; paid_at: number | null };
type ContractDay = { day: string; grams: number; status: "earned" | "below_target" | "awaiting_qa" | "failed_qa" };
type PayContract = {
  id: number; target_grams: number; amount_cents: number; start_day: string; end_day: string | null;
  daily_target_grams: number | null; daily_bonus_cents: number | null; min_qa_score: number | null;
  paid_at: number | null; grams: number; complete: boolean; amount_due_cents: number; days: ContractDay[];
  status: "proposed" | "accepted" | "declined";
};
type PayCrew = {
  player_id: number; name: string; active: 0 | 1; pay_enabled: boolean; hourly_cents: number | null;
  hours: number; grams: number; hourly_cents_total: number; piece_cents_total: number;
  bonus_cents_total: number; contract_cents_total: number; total_cents: number;
  runs: PayRun[]; bonuses: PayBonus[]; contracts: PayContract[];
};

const DAY_LABEL: Record<ContractDay["status"], string> = {
  earned: "bonus earned", below_target: "under target", awaiting_qa: "awaiting QA", failed_qa: "QA too low",
};
type Payroll = { currency: string; piece_rate_cents_per_g: number | null; total_cents: number; crew: PayCrew[] };
type Status = "unpaid" | "paid" | "all";

const PAYROLL_KEY = ["payroll"];

async function errText(res: Response, fallback: string): Promise<string> {
  const body = await res.json().catch(() => ({})) as { error?: string };
  return body.error ?? fallback;
}

// "YYYY-MM-DD" → epoch seconds at local midnight (or 23:59:59 for the end).
function dayToEpoch(day: string, endOfDay: boolean): number {
  const [y, m, d] = day.split("-").map(Number);
  const dt = endOfDay ? new Date(y, m - 1, d, 23, 59, 59) : new Date(y, m - 1, d);
  return Math.floor(dt.getTime() / 1000);
}

function fmtDuration(ms: number): string {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return h ? `${h}h ${m}m` : `${m}m`;
}

// Takes epoch MS: runs' started_at is ms, bonuses' created_at is seconds (×1000 at the call site).
function fmtWhen(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function PaidPill({ paidAt }: { paidAt: number | null }) {
  return <span className={`pill${paidAt ? "" : " live-pill"}`}>{paidAt ? "Paid" : "Unpaid"}</span>;
}

export function PayTab() {
  const [status, setStatus] = useState<Status>("unpaid");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const params = new URLSearchParams({ status });
  if (from) params.set("from", String(dayToEpoch(from, false)));
  if (to) params.set("to", String(dayToEpoch(to, true)));

  const { data, isLoading } = useQuery({
    queryKey: [...PAYROLL_KEY, status, from, to],
    queryFn: () => api.get(`/api/payroll?${params}`).then((r) => readJson<Payroll>(r)),
  });

  // Everyone with pay on gets a card (so a contract can be set up before any runs);
  // the Paid view only shows people with paid items.
  const crew = (data?.crew || []).filter((c) => {
    const items = c.runs.length || c.bonuses.length || c.contracts.length;
    return c.pay_enabled && (status === "paid" ? items : c.active || items);
  });

  return (
    <div className="panel">
      <h2>
        Pay
        {data && (
          <span className="pill" style={{ marginLeft: "auto" }} data-testid="pay-owed-total">
            Owed {formatMoney(data.total_cents, data.currency)}
          </span>
        )}
      </h2>
      <div className="small muted" style={{ marginBottom: 10 }}>
        Hourly pay uses each person&rsquo;s active trimming time (pauses excluded) at their current rate.
        Amounts are locked in when marked paid.
        {data?.piece_rate_cents_per_g != null && ` Piece rate: ${formatMoney(data.piece_rate_cents_per_g, data.currency)}/g.`}
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
        <div className="chip-grid" role="group" aria-label="Status">
          {(["unpaid", "paid", "all"] as const).map((s) => (
            <button
              key={s} type="button" className={`chip${status === s ? " active" : ""}`}
              aria-pressed={status === s} data-testid={`pay-status-${s}`} onClick={() => setStatus(s)}
            >
              {s === "unpaid" ? "Unpaid" : s === "paid" ? "Paid" : "All"}
            </button>
          ))}
        </div>
        <input type="date" aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} />
        <span className="muted small">to</span>
        <input type="date" aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} />
        {(from || to) && (
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setFrom(""); setTo(""); }}>Clear</button>
        )}
      </div>
      {data && <PayEnabledList crew={data.crew} />}
      {isLoading && <div className="muted small">Loading…</div>}
      {data && !data.total_cents && <div className="muted small">Nothing owed for this filter.</div>}
      {data && crew.map((c) => <CrewPayCard key={c.player_id} crew={c} currency={data.currency} />)}
    </div>
  );
}

// "Trim X g for $Y", optional daily bonus that only pays on days passing the QA minimum.
function ContractForm({ playerId, busy, onSave }: { playerId: number; busy: boolean; onSave: (body: Record<string, unknown>) => void }) {
  const toast = useToast();
  const today = new Date().toLocaleDateString("en-CA");
  const [f, setF] = useState({ grams: "", amount: "", start: today, end: "", dailyGrams: "", dailyBonus: "", minQa: "7" });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  function submit() {
    const grams = Number(f.grams), amount = Number(f.amount);
    if (!(grams > 0) || !(amount > 0)) { toast("Enter the grams and the amount", "err"); return; }
    const body: Record<string, unknown> = {
      player_id: playerId, target_grams: grams, amount_cents: Math.round(amount * 100), start_day: f.start,
      end_day: f.end || null, time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
    if (f.dailyBonus) {
      Object.assign(body, {
        daily_target_grams: Number(f.dailyGrams), daily_bonus_cents: Math.round(Number(f.dailyBonus) * 100), min_qa_score: Number(f.minQa),
      });
    }
    onSave(body);
  }

  return (
    <div className="panel" style={{ marginTop: 8 }}>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <span className="small">Trim</span>
        <input type="number" min={0} aria-label="Contract grams" placeholder="grams" value={f.grams} onChange={set("grams")} style={{ maxWidth: 100 }} />
        <span className="small">g for $</span>
        <input type="number" min={0} step="0.01" aria-label="Contract amount" placeholder="amount" value={f.amount} onChange={set("amount")} style={{ maxWidth: 100 }} />
        <span className="small">from</span>
        <input type="date" aria-label="Contract start" value={f.start} onChange={set("start")} />
        <span className="small">to</span>
        <input type="date" aria-label="Contract end" value={f.end} onChange={set("end")} />
      </div>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center", marginTop: 6 }}>
        <span className="small">Daily bonus $</span>
        <input type="number" min={0} step="0.01" aria-label="Daily bonus" placeholder="optional" value={f.dailyBonus} onChange={set("dailyBonus")} style={{ maxWidth: 90 }} />
        <span className="small">for</span>
        <input type="number" min={0} aria-label="Daily target grams" placeholder="grams" value={f.dailyGrams} onChange={set("dailyGrams")} style={{ maxWidth: 90 }} />
        <span className="small">g/day if every run scores QA</span>
        <input type="number" min={1} max={10} aria-label="Minimum QA score" value={f.minQa} onChange={set("minQa")} style={{ maxWidth: 60 }} />
        <span className="small">+</span>
        <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={submit}>Propose contract</button>
      </div>
      <div className="small muted" style={{ marginTop: 4 }}>
        They get it in the app to accept or decline. Once accepted, contract runs replace hourly and piece pay, and the full amount is owed once the grams are reached.
      </div>
    </div>
  );
}

// Per-person switch: only people with pay turned on get pay cards and count toward "Owed".
function PayEnabledList({ crew }: { crew: PayCrew[] }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  // Optimistic value while the save + refetch is in flight.
  const [pending, setPending] = useState<{ id: number; enabled: boolean } | null>(null);

  async function setEnabled(c: PayCrew, enabled: boolean) {
    setPending({ id: c.player_id, enabled });
    try {
      const res = await api.putRaw(`/api/players/${c.player_id}/pay-enabled`, JSON.stringify({ enabled }), { "Content-Type": "application/json" });
      if (!res.ok) { toast(await errText(res, "Could not update"), "err"); return; }
      await queryClient.invalidateQueries({ queryKey: PAYROLL_KEY });
      toast(`${c.name}: pay ${enabled ? "on" : "off"}`, "ok");
    } finally {
      setPending(null);
    }
  }

  return (
    <details className="panel" style={{ marginBottom: 10 }} open={!crew.some((c) => c.pay_enabled)}>
      <summary><b>Who gets paid</b> <span className="muted small">({crew.filter((c) => c.pay_enabled).length} of {crew.length} on)</span></summary>
      <div className="small muted" style={{ margin: "6px 0" }}>Turn pay on for each person you pay through Trim Log.</div>
      <div className="chip-grid">
        {crew.map((c) => (
          <label key={c.player_id} className="chip" style={{ gap: 6 }}>
            <input
              type="checkbox" checked={pending?.id === c.player_id ? pending.enabled : c.pay_enabled} disabled={pending != null}
              aria-label={`Pay ${c.name}`} onChange={(e) => void setEnabled(c, e.target.checked)}
            />
            {c.name}
          </label>
        ))}
      </div>
    </details>
  );
}

function CrewPayCard({ crew: c, currency }: { crew: PayCrew; currency: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const money = (cents: number) => formatMoney(cents, currency);
  const [rate, setRate] = useState(c.hourly_cents != null ? (c.hourly_cents / 100).toFixed(2) : "");
  const [open, setOpen] = useState(false);
  const [selRuns, setSelRuns] = useState<Set<number>>(new Set());
  const [selBonuses, setSelBonuses] = useState<Set<number>>(new Set());
  const [showBonus, setShowBonus] = useState(false);
  const [bonusAmount, setBonusAmount] = useState("");
  const [bonusNote, setBonusNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [showContract, setShowContract] = useState(false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: PAYROLL_KEY });

  async function saveContract(body: Record<string, unknown>) {
    const done = await run(() => api.postJson("/api/payroll/contracts", body), "Could not propose contract", `Contract sent to ${c.name}`);
    if (done) setShowContract(false);
  }

  function toggle(set: Set<number>, setter: (s: Set<number>) => void, id: number) {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    setter(next);
  }

  async function run(fn: () => Promise<Response>, fallback: string, ok: string): Promise<boolean> {
    setBusy(true);
    try {
      const res = await fn();
      if (!res.ok) { toast(await errText(res, fallback), "err"); return false; }
      await refresh();
      toast(ok, "ok");
      return true;
    } finally {
      setBusy(false);
    }
  }

  function saveRate() {
    const dollars = rate.trim() === "" ? null : Number(rate);
    if (dollars !== null && (!Number.isFinite(dollars) || dollars < 0)) { toast("Enter an hourly rate, e.g. 25.00", "err"); return; }
    const hourly_cents = dollars === null ? null : Math.round(dollars * 100);
    void run(
      () => api.putRaw(`/api/players/${c.player_id}/hourly-rate`, JSON.stringify({ hourly_cents }), { "Content-Type": "application/json" }),
      "Could not save rate",
      hourly_cents === null ? `${c.name}: hourly rate cleared` : `${c.name}: rate saved`,
    );
  }

  async function mark(run_ids: number[], bonus_ids: number[], paid: boolean, contract_ids: number[] = []) {
    if (!run_ids.length && !bonus_ids.length && !contract_ids.length) { toast("Nothing selected", "err"); return; }
    const done = await run(
      () => api.postJson("/api/payroll/mark", { run_ids, bonus_ids, contract_ids, paid }),
      "Could not update",
      paid ? "Marked paid" : "Marked unpaid",
    );
    if (done) { setSelRuns(new Set()); setSelBonuses(new Set()); }
  }

  async function addBonus() {
    const dollars = Number(bonusAmount);
    if (!Number.isFinite(dollars) || dollars <= 0) { toast("Enter a bonus amount", "err"); return; }
    const done = await run(
      () => api.postJson("/api/payroll/bonuses", {
        player_id: c.player_id, amount_cents: Math.round(dollars * 100), note: bonusNote.trim() || undefined,
      }),
      "Could not add bonus",
      "Bonus added",
    );
    if (done) { setShowBonus(false); setBonusAmount(""); setBonusNote(""); }
  }

  const unpaidRunIds = c.runs.filter((r) => !r.paid_at).map((r) => r.id);
  const unpaidBonusIds = c.bonuses.filter((b) => !b.paid_at).map((b) => b.id);
  const owedContractIds = c.contracts.filter((k) => k.complete && !k.paid_at).map((k) => k.id);

  return (
    <div className="panel" data-testid={`pay-crew-${c.player_id}`} style={{ marginBottom: 10 }}>
      <h2>
        {c.name}
        {!c.active && <span className="pill">inactive</span>}
        <span className="pill" style={{ marginLeft: "auto" }}>{money(c.total_cents)}</span>
      </h2>
      <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          type="number" inputMode="decimal" min={0} step="0.01" placeholder="none" aria-label="Hourly rate"
          value={rate} onChange={(e) => setRate(e.target.value)} style={{ maxWidth: 110 }}
        />
        <span className="muted small">per hour</span>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={saveRate}>Save</button>
      </div>
      <div className="small muted" style={{ margin: "8px 0" }}>
        {c.hours.toFixed(2)} h · {c.grams.toFixed(0)} g · hourly {money(c.hourly_cents_total)} · piece {money(c.piece_cents_total)}
        {" "}· bonuses {money(c.bonus_cents_total)} · <b>total {money(c.total_cents)}</b>
      </div>
      <div className="row" style={{ gap: 6, flexWrap: "wrap" }}>
        <button
          type="button" className="btn btn-primary btn-sm"
          disabled={busy || (!unpaidRunIds.length && !unpaidBonusIds.length && !owedContractIds.length)}
          onClick={() => void mark(unpaidRunIds, unpaidBonusIds, true, owedContractIds)}
        >
          Mark all paid
        </button>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void mark([...selRuns], [...selBonuses], true)}>
          Mark selected paid
        </button>
        <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void mark([...selRuns], [...selBonuses], false)}>
          Mark selected unpaid
        </button>
        <button type="button" className="btn btn-sm" onClick={() => setShowBonus(!showBonus)}>Add bonus</button>
        <button type="button" className="btn btn-sm" onClick={() => setShowContract(!showContract)}>New contract</button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(!open)}>
          {open ? "Hide" : "Show"} {c.runs.length} runs · {c.bonuses.length} bonuses
        </button>
      </div>

      {showBonus && (
        <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          <input
            type="number" inputMode="decimal" min={0} step="0.01" placeholder="Amount" aria-label="Bonus amount"
            value={bonusAmount} onChange={(e) => setBonusAmount(e.target.value)} style={{ maxWidth: 110 }}
          />
          <input placeholder="Note (optional)" aria-label="Bonus note" value={bonusNote} onChange={(e) => setBonusNote(e.target.value)} />
          <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void addBonus()}>Add</button>
        </div>
      )}

      {showContract && <ContractForm playerId={c.player_id} busy={busy} onSave={saveContract} />}

      {c.contracts.map((k) => (
        <div key={k.id} className="panel" data-testid={`pay-contract-${k.id}`} style={{ marginTop: 8 }}>
          <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <b>Contract: {k.target_grams.toFixed(0)} g for {money(k.amount_cents)}</b>
            <span className="muted small">{k.start_day} → {k.end_day ?? "open"}</span>
            <span className={`pill${k.paid_at ? "" : k.complete ? " live-pill" : ""}`} style={{ marginLeft: "auto" }} data-testid={`pay-contract-status-${k.id}`}>
              {k.status === "proposed" ? `Waiting for ${c.name} to accept` : k.status === "declined" ? `Declined by ${c.name}`
                : k.paid_at ? "Paid" : k.complete ? "Complete — owed" : "In progress"}
            </span>
          </div>
          <progress value={Math.min(k.grams, k.target_grams)} max={k.target_grams} style={{ width: "100%" }} />
          <div className="small muted">
            {k.grams.toFixed(0)} / {k.target_grams.toFixed(0)} g
            {k.daily_bonus_cents != null && ` · daily bonus ${money(k.daily_bonus_cents)} for ${k.daily_target_grams} g/day at QA ${k.min_qa_score}+`}
          </div>
          {k.days.length > 0 && (
            <div className="chip-grid" style={{ marginTop: 6 }}>
              {k.days.map((d) => (
                <span key={d.day} className={`pill${d.status === "earned" ? " live-pill" : ""}`}>
                  {d.day}: {d.grams.toFixed(0)} g · {DAY_LABEL[d.status]}
                </span>
              ))}
            </div>
          )}
          <div className="row" style={{ gap: 6, marginTop: 6 }}>
            {k.complete && !k.paid_at && (
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void mark([], [], true, [k.id])}>
                Mark contract paid
              </button>
            )}
            {k.paid_at && (
              <button type="button" className="btn btn-sm" disabled={busy} onClick={() => void mark([], [], false, [k.id])}>Mark unpaid</button>
            )}
            {!k.paid_at && (
              <button
                type="button" className="btn btn-ghost btn-sm" disabled={busy}
                onClick={() => void run(() => api.del(`/api/payroll/contracts/${k.id}`), "Could not delete contract", "Contract deleted")}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      ))}

      {open && (
        <div className="table-wrap" style={{ marginTop: 8 }}>
          <table className="data-table">
            <thead>
              <tr><th /><th>When</th><th>Duration</th><th>Grams</th><th>Amount</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {c.runs.map((r) => (
                <tr key={`r${r.id}`}>
                  <td><input type="checkbox" checked={selRuns.has(r.id)} onChange={() => toggle(selRuns, setSelRuns, r.id)} /></td>
                  <td>{fmtWhen(r.started_at)}</td>
                  <td>{fmtDuration(r.active_ms)}</td>
                  <td>{r.grams.toFixed(0)} g</td>
                  <td>{money(r.hourly_cents_due + r.piece_cents_due)}</td>
                  <td><PaidPill paidAt={r.paid_at} /></td>
                  <td />
                </tr>
              ))}
              {c.bonuses.map((b) => (
                <tr key={`b${b.id}`}>
                  <td><input type="checkbox" checked={selBonuses.has(b.id)} onChange={() => toggle(selBonuses, setSelBonuses, b.id)} /></td>
                  <td>{fmtWhen(b.created_at * 1000)}</td>
                  <td colSpan={2}>Bonus{b.note ? ` — ${b.note}` : ""}</td>
                  <td>{money(b.amount_cents)}</td>
                  <td><PaidPill paidAt={b.paid_at} /></td>
                  <td>
                    {!b.paid_at && (
                      <button
                        type="button" className="btn btn-sm btn-ghost" disabled={busy}
                        onClick={() => void run(() => api.del(`/api/payroll/bonuses/${b.id}`), "Could not delete bonus", "Bonus deleted")}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
